import { RefreshCw, ChevronRight } from "lucide-react";
import { Button } from "components/common/buttons/Button";
import { APP_UPDATE_BANNER_HEIGHT_PX } from "@/constants/appUpdateBannerLayout";
import { useAppVersion } from "hooks/useAppVersion";
import { cn } from "utils/cn";
import { ENVS } from "config/envs";
import type { AppUpdateBannerProps } from "./AppUpdateBanner.types";

const RELEASE_NOTES_MAX_LEN = 140;

function truncateReleaseNotes(text: string): string {
  const t = text.trim();
  if (t.length <= RELEASE_NOTES_MAX_LEN) return t;
  return `${t.slice(0, RELEASE_NOTES_MAX_LEN).trimEnd()}…`;
}

export const AppUpdateBanner = ({ className }: AppUpdateBannerProps) => {
  const { hasUpdate, applying, applyUpdate, latestManifest } = useAppVersion();

  if (!hasUpdate) return null;

  const releaseSnippet =
    latestManifest?.releaseNotes && latestManifest.releaseNotes.trim().length > 0
      ? truncateReleaseNotes(latestManifest.releaseNotes)
      : null;

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{ height: APP_UPDATE_BANNER_HEIGHT_PX, minHeight: APP_UPDATE_BANNER_HEIGHT_PX }}
      className={cn(
        "flex w-full shrink-0 items-center justify-between gap-4 border-b border-border bg-background px-4 text-foreground",
        className
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3">
        <span className="inline-flex shrink-0 items-center gap-2 text-sm font-medium text-primary">
          <span
            className="inline-flex h-2 w-2 shrink-0 rounded-full bg-primary shadow-[0_0_8px_theme(colors.amber.400)]"
            aria-hidden
          />
          New version available
        </span>
        {releaseSnippet ? (
          <span className="min-w-0 truncate text-sm text-muted-foreground">{releaseSnippet}</span>
        ) : (
          <span className="text-sm text-muted-foreground">
            Reload to get the latest RivetAI improvements and fixes.
          </span>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {ENVS.changelogUrl ? (
          <a
            href={ENVS.changelogUrl}
            target="_blank"
            rel="noreferrer"
            className="hidden items-center gap-1 text-sm text-muted-foreground underline-offset-4 hover:text-foreground sm:inline-flex"
          >
            What&apos;s new
            <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
          </a>
        ) : null}
        <Button
          variant="badge"
          color="brand"
          size="sm"
          className="h-9 shrink-0 px-4 text-sm"
          onClick={() => void applyUpdate()}
          isLoading={applying}
        >
          <span className="inline-flex items-center gap-2">
            <RefreshCw className="h-4 w-4" aria-hidden />
            Update now
          </span>
        </Button>
      </div>
    </div>
  );
};
